import axios from 'axios'
import { Configuration } from './configuration'
import { AuthApi, AcademicYearsApi, ClassesApi, SettingsApi, TermsApi, SubscriptionsApi, MarkingScalesApi, AssessmentCategoriesApi } from './api'
import type { User, Lesson, ClassDetails, Student, Term } from './api'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

export function resolveFileUrl(url: string): string {
  return url.startsWith('/') ? `${API_BASE_URL}${url}` : url
}

// Shared axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'web',
  },
  withCredentials: true,
})

// Shared promise so concurrent 401s coalesce into a single refresh call
let refreshPromise: Promise<void> | null = null

// Response interceptor — handle 401s with cookie-based token refresh.
// Mirrors the proven pattern from Bright-Class-Admin/src/api/client.ts.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Only handle 401s, and only retry once per request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Coalesce concurrent refresh attempts into one request.
        // Use raw axios (not apiClient) to avoid re-entering this interceptor.
        if (!refreshPromise) {
          refreshPromise = axios
            .post(
              `${apiClient.defaults.baseURL}/auth/refresh`,
              {},
              {
                withCredentials: true,
                headers: { 'X-Client-Type': 'web' },
              },
            )
            .then(() => {})
            .finally(() => {
              refreshPromise = null
            })
        }

        await refreshPromise

        // Cookie is now updated by the server — retry the original request
        return apiClient(originalRequest)
      } catch {
        // Refresh failed — let the error propagate back to the caller
        // (e.g. auth.init()) so it can set isInitialized = true.
        // The router guard will then naturally redirect to login.
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)

// Configuration for OpenAPI client using our axios instance
const config = new Configuration({
  basePath: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  baseOptions: { adapter: apiClient.defaults.adapter },
})

// Instantiate generated APIs
export { apiClient }
export const authApi = new AuthApi(config, config.basePath, apiClient)
export const academicYearsApi = new AcademicYearsApi(config, config.basePath, apiClient)
export const classesApi = new ClassesApi(config, config.basePath, apiClient)
export const settingsApi = new SettingsApi(config, config.basePath, apiClient)
export const termsApi = new TermsApi(config, config.basePath, apiClient)
export const subscriptionsApi = new SubscriptionsApi(config, config.basePath, apiClient)
export const markingScalesApi = new MarkingScalesApi(config, config.basePath, apiClient)
export const assessmentCategoriesApi = new AssessmentCategoriesApi(config, config.basePath, apiClient)

// ============================================================================
// Manual API additions (not in OpenAPI spec yet)
// ============================================================================

// User API - /me endpoints
export const userApi = {
  /** Get current authenticated user */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/me')
    return response.data
  },

  /** Update current user profile */
  async updateProfile(data: { name?: string }): Promise<User> {
    const response = await apiClient.patch<User>('/me', data)
    return response.data
  },

  /** Upload profile photo */
  async uploadProfilePhoto(file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)

    await apiClient.post('/me/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  /** Get combined usage overview (storage, connect, AI limits) */
  async getUsageOverview(): Promise<UsageOverview> {
    const response = await apiClient.get<UsageOverview>('/me/usage')
    return response.data
  },
}

/**
 * The four free-tier caps (post-6.0.0).
 *
 * Limits use `null` for "no cap" (Pro tier). The legacy per-feature AI
 * fields (`aiCourseGenerationsLimit` etc) all mirror `aiMonthlyLimit`
 * and stay on the response for back-compat with older clients — new
 * code should read the consolidated fields below.
 *
 * Storage limit is always a number (Pro raises it to 5 GB rather than
 * removing the cap).
 */
export interface UsageOverview {
  isPremium: boolean

  // The four gates
  classesCount: number
  classesLimit: number | null
  planningSchemesCount: number
  planningSchemesLimit: number | null
  aiMonthlyUsed: number
  aiMonthlyLimit: number | null
  storageBytesUsed: number
  storageBytesLimit: number

  storageBreakdown: {
    submissionsBytes: number
    planningResourcesBytes: number
    connectAttachmentsBytes: number
    studentPhotosBytes: number
  }

  // Legacy fields — kept for back-compat with older code paths.
  // Connect is no longer a Pro gate; these will be removed once all
  // call sites move to the consolidated fields above.
  connectSpacesEnabled: number
  connectSpacesLimit: number
  aiCourseGenerationsUsed: number
  aiCourseGenerationsLimit: number
  aiLessonGenerationsUsed: number
  aiLessonGenerationsLimit: number
  aiPlanGenerationsUsed: number
  aiPlanGenerationsLimit: number
  aiReportGenerationsUsed: number
  aiReportGenerationsLimit: number
}

// Class Planner API - /classes/{id}/planner
export interface ClassPlannerItem {
  type: 'Lesson' | 'Assignment' | 'Note'
  id: string
  dateTime: string
  endDateTime?: string
  title?: string
  room?: string
  attendancePercent?: number
  text?: string
  submittedCount?: number
  totalCount?: number
}

export interface ClassPlannerResponse {
  items: ClassPlannerItem[]
}

export const classPlannerApi = {
  /** Get class schedule items (lessons, assignments, notes) for a date range */
  async getClassPlanner(classId: string, from: string, to: string): Promise<ClassPlannerItem[]> {
    const params = new URLSearchParams({ from, to })
    const response = await apiClient.get<ClassPlannerResponse>(`/classes/${classId}/schedule`, {
      params,
    })
    return response.data.items
  },
}

// Lessons API - /lessons endpoints
export interface LessonFilters {
  classIds?: string[]
  termIds?: string[]
  date?: string // ISO date string YYYY-MM-DD
  from?: string // ISO date string
  to?: string // ISO date string
}

/** A recurring weekly slot (server 1.13.0 slot API). Times "HH:mm", dates "YYYY-MM-DD". */
export interface LessonSlot {
  id: string
  classId: string
  dayOfWeek: string
  startTime: string
  endTime: string
  room?: string
  title?: string
  seriesStart: string
  seriesEnd: string
}
/** FUTURE = split the series from a date onward; ALL = rewrite the whole series. */
export type RescheduleScope = 'FUTURE' | 'ALL'
export interface RescheduleSlotRequest {
  scope: RescheduleScope
  fromDate?: string
  rule: { dayOfWeek: string; startTime: string; endTime: string; room?: string; title?: string }
  seriesEnd?: string
}

export const lessonsApi = {
  /** Get lessons with optional filters */
  async getLessons(filters?: LessonFilters): Promise<Lesson[]> {
    const params = new URLSearchParams()
    if (filters?.classIds?.length) {
      filters.classIds.forEach((id) => params.append('classId', id))
    }
    if (filters?.termIds?.length) {
      filters.termIds.forEach((id) => params.append('termId', id))
    }
    if (filters?.date) params.set('date', filters.date)
    if (filters?.from) params.set('from', filters.from)
    if (filters?.to) params.set('to', filters.to)

    const response = await apiClient.get<Lesson[]>('/lessons', { params })
    return response.data
  },

  /** Get a single lesson by ID */
  async getLesson(lessonId: string): Promise<Lesson> {
    const response = await apiClient.get<Lesson>(`/lessons/${lessonId}`)
    return response.data
  },

  /** Create a new lesson */
  async createLesson(data: {
    classId: string
    startTime: string
    endTime: string
    room?: string
    title?: string
    tag?: string
  }): Promise<Lesson> {
    const response = await apiClient.post<Lesson>('/lessons', data)
    return response.data
  },

  /** Update a lesson */
  async updateLesson(
    lessonId: string,
    data: { startTime?: string; endTime?: string; room?: string; title?: string; tag?: string },
  ): Promise<void> {
    await apiClient.put(`/lessons/${lessonId}`, data)
  },

  /** Delete a lesson */
  async deleteLesson(lessonId: string): Promise<void> {
    await apiClient.delete(`/lessons/${lessonId}`)
  },

  /** Create a recurring lesson schedule */
  async createLessonSchedule(data: {
    classId: string
    startDate: string
    endDate: string
    description?: string
    frequency?: string
    sessions: Array<{
      dayOfWeek: string
      startTime: string
      endTime: string
      room?: string
      title?: string
    }>
  }): Promise<void> {
    await apiClient.post('/lessons/schedule', data)
  },

  /**
   * Create recurring lesson slots (server 1.13.0 slot API — replaces the
   * deprecated createLessonSchedule). Times are "HH:mm"; dates are "YYYY-MM-DD".
   */
  async createLessonSlots(data: {
    classId: string
    seriesStart: string
    seriesEnd: string
    slots: Array<{
      dayOfWeek: string
      startTime: string
      endTime: string
      room?: string
      title?: string
    }>
  }): Promise<{ lessons: Lesson[] }> {
    const response = await apiClient.post<{ slots: unknown[]; lessons: Lesson[] }>(
      '/lessons/slots',
      data,
    )
    return response.data
  },

  /** List the recurring weekly slots for a class. */
  async getLessonSlots(classId: string): Promise<LessonSlot[]> {
    const response = await apiClient.get<LessonSlot[]>('/lessons/slots', { params: { classId } })
    return response.data
  },

  /** Reschedule a recurring slot — whole series (ALL) or this-and-following (FUTURE). */
  async rescheduleSlot(slotId: string, data: RescheduleSlotRequest): Promise<{ lessons: Lesson[] }> {
    const response = await apiClient.put<{ slots: unknown[]; lessons: Lesson[] }>(
      `/lessons/slots/${slotId}`,
      data,
    )
    return response.data
  },

  /** Delete a recurring slot. keepPast keeps past/locked instances as one-offs. */
  async deleteLessonSlot(slotId: string, keepPast = true): Promise<void> {
    await apiClient.delete(`/lessons/slots/${slotId}`, { params: { keepPast } })
  },

  /** Skip (soft-cancel) a single lesson occurrence. */
  async skipLesson(lessonId: string): Promise<void> {
    await apiClient.post(`/lessons/${lessonId}/skip`)
  },

  /** Restore a previously skipped lesson occurrence. */
  async restoreLesson(lessonId: string): Promise<void> {
    await apiClient.post(`/lessons/${lessonId}/restore`)
  },
}

// Planning API - /planning endpoints

// Scheme types
export interface PlanningSchemeOverview {
  id: string
  name: string
  description?: string
  courseName?: string
  colour?: string
  iconId?: string
  unitCount: number
  lessonPlanCount: number
  createdAt?: string
  updatedAt?: string
}

export interface SchemeObjective {
  id: string
  objective: string
  position: number
}

export interface SchemeTopic {
  id: string
  topic: string
  position: number
}

export interface PlanningScheme {
  id: string
  name: string
  description?: string
  courseName?: string
  colour?: string
  iconId?: string
  objectives?: SchemeObjective[]
  topics?: SchemeTopic[]
  units: PlanningUnit[]
  createdAt?: string
  updatedAt?: string
}

// Unit types
export interface UnitObjective {
  id: string
  objective: string
  position: number
}

export interface PlanningUnit {
  id: string
  schemeId?: string
  title: string
  aim?: string
  position: number
  objectives?: UnitObjective[]
  lessonPlans?: LessonPlanOverview[]
  // Legacy field names for compatibility
  name?: string
  description?: string
  orderIndex?: number
}

// Lesson plan types
export interface LessonPlanOverview {
  id: string
  unitId?: string
  title: string
  overview?: string
  position: number
  estimatedDuration?: number
  activityCount?: number
  objectiveCount?: number
  // Legacy field names
  duration?: number
  orderIndex?: number
}

export interface LessonObjective {
  id: string
  objective: string
  position: number
}

export type LessonPhase = 'STARTER' | 'MAIN' | 'CLOSING' | 'EXTENSION' | 'HOMEWORK'

export interface LessonActivity {
  id: string
  lessonPlanId: string
  title: string
  description?: string
  phase: LessonPhase
  duration?: number
  position: number
}

export interface LessonPlan {
  id: string
  unitId: string
  unitTitle?: string
  schemeId?: string
  schemeTitle?: string
  schemeColour?: string
  title: string
  overview?: string
  position: number
  estimatedDuration?: number
  objectives?: LessonObjective[]
  activities?: LessonActivity[]
}

// Request types
export interface CreateSchemeRequest {
  name: string
  description?: string
  courseId?: string
  colour?: string
  iconId?: string
}

export interface UpdateSchemeRequest {
  name?: string
  description?: string
  courseId?: string
  colour?: string
  iconId?: string
}

export interface CreateUnitRequest {
  title: string
  aim?: string
  position?: number
}

export interface UpdateUnitRequest {
  title?: string
  aim?: string
  position?: number
}

export interface CreateLessonPlanRequest {
  title: string
  overview?: string
  position?: number
  estimatedDuration?: number
}

export interface UpdateLessonPlanRequest {
  title?: string
  overview?: string
  position?: number
  estimatedDuration?: number
}

export interface CreateActivityRequest {
  title: string
  description?: string
  phase: LessonPhase
  duration?: number
  position?: number
}

export interface UpdateActivityRequest {
  title?: string
  description?: string
  phase?: LessonPhase
  duration?: number
  position?: number
}

export const planningApi = {
  // Scheme CRUD
  async getSchemes(): Promise<PlanningSchemeOverview[]> {
    const response = await apiClient.get<PlanningSchemeOverview[]>('/planning/schemes')
    return response.data
  },

  async getScheme(schemeId: string): Promise<PlanningScheme> {
    const response = await apiClient.get<PlanningScheme>(`/planning/schemes/${schemeId}`)
    return response.data
  },

  async createScheme(data: CreateSchemeRequest): Promise<PlanningScheme> {
    const response = await apiClient.post<PlanningScheme>('/planning/schemes', data)
    return response.data
  },

  async updateScheme(schemeId: string, data: UpdateSchemeRequest): Promise<PlanningScheme> {
    const response = await apiClient.put<PlanningScheme>(`/planning/schemes/${schemeId}`, data)
    return response.data
  },

  async deleteScheme(schemeId: string): Promise<void> {
    await apiClient.delete(`/planning/schemes/${schemeId}`)
  },

  // Scheme objectives
  async addSchemeObjective(
    schemeId: string,
    objective: string,
    position?: number
  ): Promise<SchemeObjective> {
    const response = await apiClient.post<SchemeObjective>(
      `/planning/schemes/${schemeId}/objectives`,
      { objective, position }
    )
    return response.data
  },

  async deleteSchemeObjective(objectiveId: string): Promise<void> {
    await apiClient.delete(`/planning/scheme-objectives/${objectiveId}`)
  },

  // Scheme topics
  async addSchemeTopic(schemeId: string, topic: string, position?: number): Promise<SchemeTopic> {
    const response = await apiClient.post<SchemeTopic>(`/planning/schemes/${schemeId}/topics`, {
      topic,
      position,
    })
    return response.data
  },

  async deleteSchemeTopic(topicId: string): Promise<void> {
    await apiClient.delete(`/planning/scheme-topics/${topicId}`)
  },

  // Unit CRUD
  async getUnits(schemeId: string): Promise<PlanningUnit[]> {
    const response = await apiClient.get<PlanningUnit[]>(`/planning/schemes/${schemeId}/units`)
    return response.data
  },

  async getUnit(unitId: string): Promise<PlanningUnit> {
    const response = await apiClient.get<PlanningUnit>(`/planning/units/${unitId}`)
    return response.data
  },

  async createUnit(schemeId: string, data: CreateUnitRequest): Promise<PlanningUnit> {
    const response = await apiClient.post<PlanningUnit>(`/planning/schemes/${schemeId}/units`, data)
    return response.data
  },

  async updateUnit(unitId: string, data: UpdateUnitRequest): Promise<PlanningUnit> {
    const response = await apiClient.put<PlanningUnit>(`/planning/units/${unitId}`, data)
    return response.data
  },

  async deleteUnit(unitId: string): Promise<void> {
    await apiClient.delete(`/planning/units/${unitId}`)
  },

  // Unit objectives
  async addUnitObjective(
    unitId: string,
    objective: string,
    position?: number
  ): Promise<UnitObjective> {
    const response = await apiClient.post<UnitObjective>(`/planning/units/${unitId}/objectives`, {
      objective,
      position,
    })
    return response.data
  },

  async deleteUnitObjective(objectiveId: string): Promise<void> {
    await apiClient.delete(`/planning/unit-objectives/${objectiveId}`)
  },

  // Lesson plan CRUD
  async getLessonPlans(unitId: string): Promise<LessonPlanOverview[]> {
    const response = await apiClient.get<LessonPlanOverview[]>(
      `/planning/units/${unitId}/lesson-plans`
    )
    return response.data
  },

  async getLessonPlan(lessonPlanId: string): Promise<LessonPlan> {
    const response = await apiClient.get<LessonPlan>(`/planning/lesson-plans/${lessonPlanId}`)
    return response.data
  },

  async createLessonPlan(unitId: string, data: CreateLessonPlanRequest): Promise<LessonPlan> {
    const response = await apiClient.post<LessonPlan>(
      `/planning/units/${unitId}/lesson-plans`,
      data
    )
    return response.data
  },

  async updateLessonPlan(lessonPlanId: string, data: UpdateLessonPlanRequest): Promise<LessonPlan> {
    const response = await apiClient.put<LessonPlan>(`/planning/lesson-plans/${lessonPlanId}`, data)
    return response.data
  },

  async deleteLessonPlan(lessonPlanId: string): Promise<void> {
    await apiClient.delete(`/planning/lesson-plans/${lessonPlanId}`)
  },

  // Lesson plan objectives
  async addLessonObjective(
    lessonPlanId: string,
    objective: string,
    position?: number
  ): Promise<LessonObjective> {
    const response = await apiClient.post<LessonObjective>(
      `/planning/lesson-plans/${lessonPlanId}/objectives`,
      { objective, position }
    )
    return response.data
  },

  async deleteLessonObjective(objectiveId: string): Promise<void> {
    await apiClient.delete(`/planning/lesson-objectives/${objectiveId}`)
  },

  // Activity CRUD
  async getActivities(lessonPlanId: string): Promise<LessonActivity[]> {
    const response = await apiClient.get<LessonActivity[]>(
      `/planning/lesson-plans/${lessonPlanId}/activities`
    )
    return response.data
  },

  async createActivity(lessonPlanId: string, data: CreateActivityRequest): Promise<LessonActivity> {
    const response = await apiClient.post<LessonActivity>(
      `/planning/lesson-plans/${lessonPlanId}/activities`,
      data
    )
    return response.data
  },

  async updateActivity(activityId: string, data: UpdateActivityRequest): Promise<LessonActivity> {
    const response = await apiClient.put<LessonActivity>(`/planning/activities/${activityId}`, data)
    return response.data
  },

  async deleteActivity(activityId: string): Promise<void> {
    await apiClient.delete(`/planning/activities/${activityId}`)
  },

  // AI Generation
  async generateSchemeUnits(
    schemeId: string,
    data: GenerateSchemeUnitsRequest
  ): Promise<{ jobId: string }> {
    const response = await apiClient.post<{ jobId: string }>(
      `/planning/ai/schemes/${schemeId}/generate-units`,
      data
    )
    return response.data
  },

  async generateUnitLessons(
    unitId: string,
    data: GenerateUnitLessonsRequest
  ): Promise<{ jobId: string }> {
    const response = await apiClient.post<{ jobId: string }>(
      `/planning/ai/units/${unitId}/generate-lessons`,
      data
    )
    return response.data
  },

  async generateLessonPlan(
    lessonPlanId: string,
    data: GenerateLessonPlanRequest
  ): Promise<{ jobId: string }> {
    const response = await apiClient.post<{ jobId: string }>(
      `/planning/ai/lesson-plans/${lessonPlanId}/generate`,
      data
    )
    return response.data
  },

  /** Get the base URL for SSE streaming */
  getAiStreamUrl(jobId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    return `${baseUrl}/planning/ai/${jobId}/stream`
  },

  // Scheme export
  async exportScheme(schemeId: string, format: ExportFormat): Promise<void> {
    const response = await apiClient.get(`/planning/schemes/${schemeId}/export`, {
      params: { format },
      responseType: 'blob',
    })
    const contentDisposition = response.headers['content-disposition']
    let filename = `scheme.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '')
      }
    }
    downloadBlob(response.data, filename)
  },

  // Lesson plan export
  async exportLessonPlan(lessonPlanId: string, format: ExportFormat): Promise<void> {
    const response = await apiClient.get(`/planning/lesson-plans/${lessonPlanId}/export`, {
      params: { format },
      responseType: 'blob',
    })
    const contentDisposition = response.headers['content-disposition']
    let filename = `lesson-plan.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '')
      }
    }
    downloadBlob(response.data, filename)
  },

  // Scheme resources
  async getSchemeResources(schemeId: string): Promise<PlanningResourceResponse[]> {
    const response = await apiClient.get<PlanningResourceResponse[]>(
      `/planning/schemes/${schemeId}/resources`
    )
    return response.data
  },

  async attachResourceToScheme(schemeId: string, resourceId: string): Promise<void> {
    await apiClient.post(`/planning/schemes/${schemeId}/resources`, { resourceId })
  },

  async detachResourceFromScheme(schemeId: string, resourceId: string): Promise<void> {
    await apiClient.delete(`/planning/schemes/${schemeId}/resources/${resourceId}`)
  },

  // Lesson plan resources
  async getLessonPlanResources(lessonPlanId: string): Promise<PlanningResourceResponse[]> {
    const response = await apiClient.get<PlanningResourceResponse[]>(
      `/planning/lesson-plans/${lessonPlanId}/resources`
    )
    return response.data
  },

  async getAvailableResourcesForLessonPlan(lessonPlanId: string): Promise<PlanningResourceResponse[]> {
    const response = await apiClient.get<PlanningResourceResponse[]>(
      `/planning/lesson-plans/${lessonPlanId}/resources/available`
    )
    return response.data
  },

  async attachResourceToLessonPlan(lessonPlanId: string, resourceId: string, position?: number): Promise<void> {
    await apiClient.post(`/planning/lesson-plans/${lessonPlanId}/resources`, { resourceId, position })
  },

  async detachResourceFromLessonPlan(lessonPlanId: string, resourceId: string): Promise<void> {
    await apiClient.delete(`/planning/lesson-plans/${lessonPlanId}/resources/${resourceId}`)
  },

  // Resource CRUD (standalone)
  async uploadFileResource(file: File, name?: string, category?: string): Promise<PlanningResourceResponse> {
    const formData = new FormData()
    formData.append('file', file)
    if (name) formData.append('name', name)
    if (category) formData.append('category', category)
    const response = await apiClient.post<PlanningResourceResponse>(
      '/planning/resources/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  async createLinkResource(data: {
    name: string
    url: string
    category: string
    description?: string
    linkProvider?: string
  }): Promise<PlanningResourceResponse> {
    const response = await apiClient.post<PlanningResourceResponse>('/planning/resources/link', data)
    return response.data
  },

  async deleteResource(resourceId: string): Promise<void> {
    await apiClient.delete(`/planning/resources/${resourceId}`)
  },
}

// AI Generation Request Types
export interface GenerateSchemeUnitsRequest {
  numberOfUnits: number
  subject?: string
  yearGroup?: string
  curriculum?: string
  topics?: string
  guidance?: string
}

export interface GenerateUnitLessonsRequest {
  numberOfLessons: number
  subject?: string
  yearGroup?: string
  guidance?: string
}

export interface GenerateLessonPlanRequest {
  subject?: string
  yearGroup?: string
  lessonDurationMinutes?: number
  numberOfActivities?: number
  guidance?: string
  includeHomework?: boolean
  homeworkGuidance?: string
  includeExtension?: boolean
  extensionGuidance?: string
}

// Assignments API
export interface AssignmentInfo {
  id: string
  assessmentId?: string
  assessmentName?: string
  dateAssigned?: string
  dateDue?: string
  weight?: number
  excludeFromFinalGrade?: boolean
  markingMethod?: MarkingMethod
  category?: AssessmentCategory
}

export interface AssignmentSubmissionSummary {
  id: string
  grade?: number
  markedAt?: string
  marking?: SubmissionMarking
  latestComment?: string
}

export interface AssignmentStudent {
  student: {
    id: string
    firstName?: string
    middleName?: string
    lastName?: string
    displayName?: string
    schoolId?: string
    photoUrl?: string
  }
  grade?: AssignmentSubmissionSummary
}

export interface AssignmentDetailsData {
  assignment: AssignmentInfo
  classDetails: {
    id: string
    name?: string
    colour?: string
    studentCount?: number
  }
  students: AssignmentStudent[]
  scale?: Scale
}

export interface AssignmentDetailsResponse {
  assignment: AssignmentDetailsData
}

// Schedule API - /schedule endpoint
export interface ScheduleItem {
  id: string
  type: 'lesson' | 'assignment' | 'event'
  title?: string
  dateTime: string
  endDateTime?: string
  classId?: string
  className?: string
  classColour?: string
  room?: string
  dateDue?: string
  location?: string
  allDay?: boolean
  /** Lesson-only. Rounded % present-or-late once attendance has been
   *  recorded; null until then. Lets the UI swap "Take register" for
   *  "X% present" on past lessons. */
  attendancePercent?: number | null
}

export interface EventItem {
  id: string
  title: string
  startDateTime: string
  endDateTime?: string
  allDay?: boolean
  location?: string
  description?: string
  classId?: string
  className?: string
  classColour?: string
  createdBy?: string
  createdAt?: string
}

export interface ScheduleResponse {
  items: ScheduleItem[]
  lessonCount: number
  assignmentCount: number
  eventCount: number
}

export interface ScheduleFilters {
  from?: string
  to?: string
  include?: string // 'lessons', 'assignments', or 'lessons,assignments'
}

export const scheduleApi = {
  /** Get the teacher's schedule (lessons and assignments) */
  async getSchedule(filters?: ScheduleFilters): Promise<ScheduleResponse> {
    const params = new URLSearchParams()
    if (filters?.from) params.set('from', filters.from)
    if (filters?.to) params.set('to', filters.to)
    if (filters?.include) params.set('include', filters.include)

    const response = await apiClient.get<ScheduleResponse>('/schedule', { params })
    return response.data
  },
}

// Events API - /events endpoints
export const eventsApi = {
  async createEvent(data: {
    title: string
    description?: string
    startTime: string
    endTime?: string
    allDay?: boolean
    location?: string
    classIds?: string[]
    spaceId?: string
  }) {
    const res = await apiClient.post('/events', data)
    return res.data
  },

  async getEvents(filters?: { from?: string; to?: string }) {
    const res = await apiClient.get('/events', { params: filters })
    return res.data as EventItem[]
  },

  async deleteEvent(eventId: string) {
    await apiClient.delete(`/events/${eventId}`)
  },
}

export const assignmentsApi = {
  /** Get assignment details with student grades */
  async getAssignmentDetails(assignmentId: string): Promise<AssignmentDetailsData> {
    const response = await apiClient.get<AssignmentDetailsResponse>(
      `/assignments/${assignmentId}/details`
    )
    // Unwrap the nested `assignment` property
    return response.data.assignment
  },
}

// Students API - additional endpoints
export interface StudentSummary {
  id: string
  firstName: string
  lastName?: string
  photoUrl?: string
  dob?: string
  classes?: ClassDetails[]
}

export interface UpcomingBirthdaysResponse {
  students: StudentSummary[]
}

export const studentsApi = {
  /** Get a student by ID */
  async getStudent(studentId: string): Promise<Student> {
    const response = await apiClient.get<Student>(`/students/${studentId}`)
    return response.data
  },

  /** Get upcoming student birthdays */
  async getUpcomingBirthdays(): Promise<StudentSummary[]> {
    const response = await apiClient.get<UpcomingBirthdaysResponse>('/students/birthdays/upcoming')
    return response.data.students ?? []
  },

  /** Search students */
  async searchStudents(query: string): Promise<StudentSummary[]> {
    const response = await apiClient.get<StudentSummary[]>('/students/search', {
      params: { q: query },
    })
    return response.data
  },

  /** Update a student */
  async updateStudent(studentId: string, data: import('./api').UpdateStudentRequest): Promise<Student> {
    const response = await apiClient.put<Student>(`/students/${studentId}`, data)
    return response.data
  },

  /** Delete a student permanently */
  async deleteStudent(studentId: string): Promise<void> {
    await apiClient.delete(`/students/${studentId}`)
  },

  /** Remove a student from a class */
  async removeFromClass(classId: string, studentId: string): Promise<void> {
    await apiClient.delete(`/classes/${classId}/students/${studentId}`)
  },

  /** Get academic overview for a student */
  async getAcademicOverview(studentId: string): Promise<import('./api').StudentAcademicOverviewResponse> {
    const response = await apiClient.get<import('./api').StudentAcademicOverviewResponse>(`/students/${studentId}/academic-overview`)
    return response.data
  },

  /** Upload student profile photo */
  async uploadStudentPhoto(studentId: string, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    await apiClient.post(`/students/${studentId}/images/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// School Types - for onboarding templates
export type SchoolType =
  | 'NURSERY'
  | 'PRIMARY'
  | 'SECONDARY'
  | 'PRIMARY_SECONDARY'
  | 'SIXTH_FORM'
  | 'SECONDARY_SIXTH'

export interface SchoolTypeOption {
  type: SchoolType
  displayName: string
  description: string
  teachingYears: string[]
  subjects: string[]
}

export interface SchoolTypeTemplatesResponse {
  schoolTypes: SchoolTypeOption[]
}

// Onboarding API - school type templates
export const onboardingApi = {
  /** Get available school type templates for onboarding */
  async getSchoolTypes(): Promise<SchoolTypeTemplatesResponse> {
    const response = await apiClient.get<SchoolTypeTemplatesResponse>('/onboarding/school-types')
    return response.data
  },
}

// Tenants API - onboarding endpoints
export interface BootstrapTenantRequest {
  name?: string
  type: 'INDIVIDUAL' | 'SCHOOL'
  schoolType?: SchoolType
  firstAcademicYear: {
    label: string
    startsAt: string // YYYY-MM-DD
    endsAt: string // YYYY-MM-DD
  }
  subjects?: string[]
  teachingYears?: string[]
  assessmentCategories?: string[]
}

export interface Tenant {
  id: string
  name?: string
  type: 'INDIVIDUAL' | 'SCHOOL'
  schoolType?: SchoolType
  createdBy: string
  createdAt: string
}

export const tenantsApi = {
  /** Bootstrap a new tenant with initial data (onboarding) */
  async bootstrap(data: BootstrapTenantRequest): Promise<Tenant> {
    const response = await apiClient.post<Tenant>('/tenants/bootstrap', data)
    return response.data
  },

  /** Get current user's tenant */
  async getCurrentTenant(): Promise<Tenant | null> {
    try {
      const response = await apiClient.get<Tenant>('/tenants/me')
      return response.data
    } catch {
      return null
    }
  },

  async updateTenant(id: string, data: { name?: string }): Promise<Tenant> {
    const response = await apiClient.put<Tenant>(`/tenants/${id}`, data)
    return response.data
  },
}

// Imports API - student import from file
export type StudentField =
  | 'FIRST_NAME'
  | 'MIDDLE_NAME'
  | 'LAST_NAME'
  | 'SCHOOL_ID'
  | 'DOB'
  | 'EMAIL'
  | 'GENDER'
  | 'MOBILE'
  | 'NATIONALITY'

export interface ParseFileResponse {
  parseId: string
  fileName: string
  fileType: 'CSV' | 'XLS' | 'XLSX'
  headers: string[]
  rowCount: number
  previewRows: Record<string, string>[]
  suggestedMappings: Record<string, StudentField | null>
  warnings: string[]
}

export interface ImportError {
  rowNumber: number
  message: string
}

export interface ImportResult {
  /** Number of new students created */
  importedCount: number
  /** Number of existing students added to the class (matched by school ID) */
  existingAddedCount?: number
  /** Number of rows skipped due to errors */
  skippedCount: number
  errors: ImportError[]
}

export const importsApi = {
  /** Parse a file and get headers with auto-suggested mappings */
  async parseFile(file: File): Promise<ParseFileResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileName', file.name)
    formData.append('contentType', file.type)

    const response = await apiClient.post<ParseFileResponse>('/imports/students/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  /** Confirm import with column mappings */
  async confirmImport(data: {
    parseId: string
    classId: string
    columnMappings: Record<string, StudentField>
  }): Promise<ImportResult> {
    const response = await apiClient.post<ImportResult>('/imports/students/confirm', data)
    return response.data
  },
}

// Register/Attendance API
export type AttendanceState = 'PRESENT' | 'ABSENT' | 'LATE'

export interface AttendanceRecord {
  studentId: string
  lessonId: string
  state: AttendanceState
  reasonId?: string
  note?: string
  recordedBy?: string
  recordedAt?: string
}

export interface AttendanceSummary {
  averagePresentPercent: number
  breakdown: Record<AttendanceState, number>
}

export interface StudentWithAttendance {
  student: {
    id: string
    firstName?: string
    middleName?: string
    lastName?: string
    displayName?: string
    schoolId?: string
    photoUrl?: string
  }
  attendance: AttendanceRecord[]
  summary: AttendanceSummary
}

export interface RegisterLesson {
  id: string
  startTime: string
  endTime?: string
  room?: string
  title?: string
  tag?: string
}

export interface ClassRegister {
  classId: string
  termId?: string
  lessons: RegisterLesson[]
  students: StudentWithAttendance[]
  reasons?: AttendanceReason[]
}

export const registerApi = {
  /** Get class attendance register */
  async getClassRegister(classId: string, termId?: string): Promise<ClassRegister> {
    const params = termId ? { termId } : {}
    const response = await apiClient.get<{ register: ClassRegister }>(
      `/classes/${classId}/register`,
      { params }
    )
    return response.data.register
  },

  /** Record attendance for a lesson */
  async recordAttendance(
    lessonId: string,
    entries: Array<{ studentId: string; state: AttendanceState }>
  ): Promise<void> {
    await apiClient.put(`/lessons/${lessonId}/attendance`, { entries })
  },
}

// Lesson Attendance API
export interface AttendanceReason {
  id: string
  code: string
  label: string
  category: 'AUTHORISED' | 'UNAUTHORISED' | 'NOT_REQUIRED'
  appliesToAbsent: boolean
  appliesToLate: boolean
  sortOrder: number
}

export interface StudentAttendanceEntry {
  student: {
    id: string
    firstName?: string
    middleName?: string
    lastName?: string
    displayName?: string
    schoolId?: string
    photoUrl?: string
  }
  attendance?: {
    status?: AttendanceState
    reasonId?: string
    note?: string
  }
}

export interface LessonAttendanceResponse {
  lesson: {
    id: string
    startTime: string
    endTime?: string
    title?: string
    room?: string
    slotId?: string // present when this lesson belongs to a recurring slot
    skipped?: boolean
    classDetails?: {
      id: string
      name?: string
      colour?: string
    }
  }
  students: StudentAttendanceEntry[]
  reasons: AttendanceReason[]
  recordedAt?: string
}

export const lessonAttendanceApi = {
  /** Get attendance for a specific lesson */
  async getLessonAttendance(lessonId: string): Promise<LessonAttendanceResponse> {
    const response = await apiClient.get<{ lessonAttendance: LessonAttendanceResponse }>(
      `/lessons/${lessonId}/attendance`
    )
    return response.data.lessonAttendance
  },

  /** Save attendance for a lesson */
  async saveAttendance(
    lessonId: string,
    entries: Array<{ studentId: string; state: AttendanceState; reasonId?: string; note?: string }>
  ): Promise<void> {
    await apiClient.put(`/lessons/${lessonId}/attendance`, { entries })
  },

  /** Get all attendance reason codes for the tenant */
  async getReasons(): Promise<AttendanceReason[]> {
    const response = await apiClient.get<{ reasons: AttendanceReason[] }>('/attendance/reasons')
    return response.data.reasons ?? []
  },
}

// Global Search API
export interface SearchResultStudent {
  id: string
  firstName?: string
  lastName?: string
  displayName?: string
  schoolId?: string
  photoUrl?: string
}

export interface SearchResultClass {
  id: string
  name?: string
  colour?: string
  studentCount: number
}

export interface SearchResultPlanningScheme {
  id: string
  name: string
  description?: string
  courseName?: string
  colour?: string
  unitCount: number
}

export interface SearchResultAssignment {
  id: string
  name: string
  className?: string
  classId?: string
  dateDue?: string
}

export interface GlobalSearchResponse {
  query: string
  students: SearchResultStudent[]
  classes: SearchResultClass[]
  planningSchemes: SearchResultPlanningScheme[]
  assignments: SearchResultAssignment[]
}

export const searchApi = {
  /** Global search across students, classes, planning, and assignments */
  async search(query: string): Promise<GlobalSearchResponse> {
    const response = await apiClient.get<GlobalSearchResponse>('/search', {
      params: { q: query },
    })
    return response.data
  },
}

// Gradebook API
export type MarkingType = 'INTEGER' | 'DECIMAL' | 'PERCENT' | 'SCALE'

export interface MarkingMethod {
  type: MarkingType
  maxPoints?: number
  scaleId?: string
}

export interface AssessmentCategory {
  id: string
  name: string
  colour?: string
}

export interface GradebookAssignment {
  id: string
  assessmentId: string
  assessmentName: string
  weight: number
  dateAssigned: string
  dateDue?: string
  excludeFromFinalGrade: boolean
  classId: string
  markingMethod: MarkingMethod
  category?: AssessmentCategory
}

export interface ScaleLevel {
  id: string
  text: string
  weight: number
}

export interface Scale {
  id: string
  name: string
  levels: ScaleLevel[]
}

export interface SubmissionMarking {
  type: 'INTEGER' | 'DECIMAL' | 'PERCENT' | 'SCALE'
  score?: number
  maxPoints?: number
  percent?: number
  level?: ScaleLevel
}

export interface SubmissionComment {
  id: string
  body: string
  authorRole?: string
  createdAt: string
}

export interface GradebookSubmission {
  id: string
  studentId: string
  markedAt?: string
  markedBy?: string
  comments: SubmissionComment[]
  assignment: {
    id: string
    assessmentName: string
  }
  marking?: SubmissionMarking
  grade?: number // normalized 0-1
}

export interface FinalGrade {
  studentId: string
  percentage?: number
  letter?: string
}

export interface CategoryGrade {
  category: AssessmentCategory
  percentage?: number
  letter?: string
}

export interface StudentGrades {
  studentId: string
  finalGrade?: FinalGrade
  categoryGrades: CategoryGrade[]
}

export interface GradebookStudent {
  id: string
  firstName?: string
  middleName?: string
  lastName?: string
  displayName?: string
  schoolId?: string
  photoUrl?: string
}

export interface Gradebook {
  assignments: GradebookAssignment[]
  submissions: GradebookSubmission[]
  students: GradebookStudent[]
  gradingSetup?: unknown
  studentGrades: StudentGrades[]
  scales: Scale[]
}

export interface CreateClassAssignmentRequest {
  assessmentName: string
  categoryId?: string
  markingType: MarkingType
  maxPoints?: number
  scaleId?: string
  weight: number
  dateAssigned: string
  dateDue?: string
  excludeFromFinalGrade: boolean
}

/** Lightweight per-cell mark from /gradebook/marks — carries the comment indicator. */
export interface GradebookMark {
  id: string
  studentId: string
  assignmentId: string
  displayText: string
  grade?: number
  hasComment: boolean
}

export interface UpsertSubmissionRequest {
  assignmentId: string
  studentId: string
  numericPoints?: number
  scaleLevelId?: string
  comment?: string
}

export const gradebookApi = {
  /** Get full gradebook for a class */
  async getGradebook(classId: string): Promise<Gradebook> {
    const response = await apiClient.get<Gradebook & { grades?: GradebookSubmission[] }>(
      `/classes/${classId}/gradebook`
    )
    const data = response.data
    // The REST payload carries per-student marks under `grades`; the rest of the
    // app reads `submissions`. Normalise here so every consumer sees one shape.
    return { ...data, submissions: data.submissions ?? data.grades ?? [] }
  },

  /** Compact per-cell marks (display text, normalized grade, comment indicator). */
  async getGradebookMarks(classId: string): Promise<GradebookMark[]> {
    const response = await apiClient.get<{ marks: GradebookMark[] }>(
      `/classes/${classId}/gradebook/marks`
    )
    return response.data.marks ?? []
  },

  /** Create an assignment for a class */
  async createAssignment(
    classId: string,
    data: CreateClassAssignmentRequest
  ): Promise<GradebookAssignment> {
    const response = await apiClient.post<GradebookAssignment>(
      `/classes/${classId}/assignments`,
      data
    )
    return response.data
  },

  /** Upsert a grade (record or update a grade) */
  async upsertSubmission(data: UpsertSubmissionRequest): Promise<GradebookSubmission> {
    const response = await apiClient.put<GradebookSubmission>('/grades', data)
    return response.data
  },

  /** Delete a grade */
  async deleteSubmission(submissionId: string): Promise<void> {
    await apiClient.delete(`/grades/${submissionId}`)
  },

  /** Get assessment categories for a tenant */
  async getCategories(): Promise<AssessmentCategory[]> {
    const response = await apiClient.get<{ categories: AssessmentCategory[] }>(
      '/assessment-categories'
    )
    return response.data.categories ?? []
  },

  /** Get marking scales for a tenant */
  async getScales(): Promise<Scale[]> {
    const response = await apiClient.get<Scale[]>('/marking-scales')
    return response.data ?? []
  },
}

// Export API - download files
export type ExportFormat = 'pdf' | 'docx' | 'xlsx'

export const exportApi = {
  /** Export class student list */
  async exportStudents(classId: string, format: ExportFormat): Promise<void> {
    const response = await apiClient.get(`/classes/${classId}/students/export`, {
      params: { format },
      responseType: 'blob',
    })

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = `students.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '')
      }
    }

    // Trigger download
    downloadBlob(response.data, filename)
  },

  /** Export gradebook (when backend supports it) */
  async exportGradebook(classId: string, format: ExportFormat): Promise<void> {
    const response = await apiClient.get(`/classes/${classId}/gradebook/export`, {
      params: { format },
      responseType: 'blob',
    })

    const contentDisposition = response.headers['content-disposition']
    let filename = `gradebook.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '')
      }
    }

    downloadBlob(response.data, filename)
  },

  /** Export attendance register (when backend supports it) */
  async exportRegister(classId: string, format: ExportFormat): Promise<void> {
    const response = await apiClient.get(`/classes/${classId}/register/export`, {
      params: { format },
      responseType: 'blob',
    })

    const contentDisposition = response.headers['content-disposition']
    let filename = `register.${format}`
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        filename = match[1].replace(/['"]/g, '')
      }
    }

    downloadBlob(response.data, filename)
  },
}

// Planning Resources
export type PlanningResourceType = 'FILE' | 'LINK'
export type PlanningResourceCategory = 'MEDIA' | 'PRESENTATION' | 'DOCUMENT' | 'LINK'

export interface PlanningResourceResponse {
  id: string
  name: string
  description?: string
  resourceType: string
  category: string
  fileName?: string
  contentType?: string
  fileSize?: number
  viewUrl?: string
  downloadUrl?: string
  url?: string
  linkTitle?: string
  linkThumbnailUrl?: string
  linkDescription?: string
  linkProvider?: string
  createdBy: string
  createdAt: string
}

// File Submissions API - /submissions endpoints
export interface FileSubmission {
  id: string
  assignmentId: string
  studentId: string
  fileName: string
  contentType: string
  fileSize: number
  viewUrl: string
  downloadUrl: string
  submittedAt: string
  submittedBy: string
  notes?: string
}

export const ALLOWED_SUBMISSION_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

export const MAX_SUBMISSION_SIZE = 25 * 1024 * 1024 // 25 MB

export const submissionsApi = {
  /** Upload a file submission for a student */
  async upload(
    assignmentId: string,
    studentId: string,
    file: File,
    notes?: string,
    onProgress?: (percent: number) => void
  ): Promise<FileSubmission> {
    const formData = new FormData()
    formData.append('file', file)
    if (notes) formData.append('notes', notes)

    const response = await apiClient.post<FileSubmission>(
      `/assignments/${assignmentId}/students/${studentId}/submissions`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        },
      }
    )
    return response.data
  },

  /** List submissions with optional filters */
  async list(params: { assignmentId?: string; studentId?: string }): Promise<FileSubmission[]> {
    const response = await apiClient.get<FileSubmission[]>('/submissions', { params })
    return response.data
  },

  /** Get a single submission with signed download URL */
  async get(submissionId: string): Promise<FileSubmission> {
    const response = await apiClient.get<FileSubmission>(`/submissions/${submissionId}`)
    return response.data
  },

  /** Delete a submission (DB + GCS) */
  async delete(submissionId: string): Promise<void> {
    await apiClient.delete(`/submissions/${submissionId}`)
  },
}

// ============================================================================
// Notes APIs
// ============================================================================

export interface Note {
  id?: string
  title?: string
  text?: string
  date?: string
}

export interface CreateNoteRequest {
  title?: string
  text: string
  date: string // ISO LocalDateTime required by backend
}

export interface UpdateNoteRequest {
  title?: string
  text: string
}

// Class notes — GET wraps in { notes: [] }
export const classNotesApi = {
  async getNotes(classId: string): Promise<Note[]> {
    const response = await apiClient.get<{ notes: Note[] }>(`/classes/${classId}/notes`)
    return response.data.notes ?? []
  },

  async createNote(classId: string, data: CreateNoteRequest): Promise<Note> {
    const response = await apiClient.post<Note>(`/classes/${classId}/notes`, data)
    return response.data
  },

  async updateNote(classId: string, noteId: string, data: UpdateNoteRequest): Promise<Note> {
    const response = await apiClient.put<Note>(`/classes/${classId}/notes/${noteId}`, data)
    return response.data
  },

  async deleteNote(classId: string, noteId: string): Promise<void> {
    await apiClient.delete(`/classes/${classId}/notes/${noteId}`)
  },
}

// Student guardians
export interface StudentGuardianResponse {
  studentId: string
  guardianId: string
  guardianshipType: string
  guardianName: string
  guardianEmail?: string
  guardianMobile?: string
  linkedStudentCount?: number
  hasUserAccount?: boolean
}

export const guardiansApi = {
  async getForStudent(studentId: string): Promise<StudentGuardianResponse[]> {
    const response = await apiClient.get<StudentGuardianResponse[]>(`/students/${studentId}/guardians`)
    return response.data
  },

  async createAndLink(
    studentId: string,
    data: { name: string; email: string; guardianshipType: string; mobile?: string }
  ): Promise<void> {
    await apiClient.post(`/students/${studentId}/guardians`, data)
  },

  async updateGuardian(studentId: string, guardianId: string, data: { name: string; email?: string; mobile?: string; guardianshipType?: string }): Promise<StudentGuardianResponse> {
    const response = await apiClient.patch<StudentGuardianResponse>(`/students/${studentId}/guardians/${guardianId}`, data)
    return response.data
  },

  async unlink(studentId: string, guardianId: string): Promise<void> {
    await apiClient.delete(`/students/${studentId}/guardians/${guardianId}`)
  },
}

// Student notes — GET returns Note[] directly
export const studentNotesApi = {
  async getNotes(studentId: string): Promise<Note[]> {
    const response = await apiClient.get<Note[]>(`/students/${studentId}/notes`)
    return response.data
  },

  async createNote(studentId: string, data: CreateNoteRequest): Promise<Note> {
    const response = await apiClient.post<Note>(`/students/${studentId}/notes`, data)
    return response.data
  },

  async updateNote(studentId: string, noteId: string, data: UpdateNoteRequest): Promise<Note> {
    const response = await apiClient.put<Note>(`/students/${studentId}/notes/${noteId}`, data)
    return response.data
  },

  async deleteNote(studentId: string, noteId: string): Promise<void> {
    await apiClient.delete(`/students/${studentId}/notes/${noteId}`)
  },
}

// Lesson notes — GET/POST under /lessons/{lessonId}/notes
// but UPDATE/DELETE at /lessons/notes/{noteId} (backend quirk — no lessonId needed)
export const lessonNotesApi = {
  async getNotes(lessonId: string): Promise<Note[]> {
    const response = await apiClient.get<Note[]>(`/lessons/${lessonId}/notes`)
    return response.data
  },

  async createNote(lessonId: string, data: CreateNoteRequest): Promise<Note> {
    const response = await apiClient.post<Note>(`/lessons/${lessonId}/notes`, data)
    return response.data
  },

  async updateNote(_lessonId: string, noteId: string, data: UpdateNoteRequest): Promise<Note> {
    const response = await apiClient.put<Note>(`/lessons/notes/${noteId}`, data)
    return response.data
  },

  async deleteNote(_lessonId: string, noteId: string): Promise<void> {
    await apiClient.delete(`/lessons/notes/${noteId}`)
  },
}

// ============================================================================
// Reports API
// ============================================================================

export interface ClassReportSummary {
  classId?: string
  className?: string
  term?: { id?: string; label?: string }
  totalStudents: number
  submittedReports: number
}

export interface StudentReportDto {
  id: string
  termId?: string
  classId: string
  studentId: string
  title: string
  comment: string
  position: number
}

export interface StudentWithReport {
  student: {
    id: string
    firstName?: string
    middleName?: string
    lastName?: string
    displayName?: string
    photoUrl?: string
    schoolId?: string
  }
  report?: StudentReportDto
  finalGradePercentage?: number
  finalGradeLetter?: string
  attendancePercentage?: number
}

export interface ClassReportResponse {
  summary: ClassReportSummary
  students: StudentWithReport[]
}

export interface GenerateReportsRequest {
  classId: string
  studentIds: string[]
  termId?: string
  customiser?: string
}

export interface ReportContentEvent {
  studentId: string
  reportId?: string
  title: string
  comment: string
  persisted: boolean
}

export interface StudentReportCard {
  student: {
    id: string
    firstName?: string
    lastName?: string
    displayName?: string
    photoUrl?: string
  }
  classDetails?: {
    id: string
    name?: string
    colour?: string
  }
  term?: { id?: string; label?: string }
  report?: {
    id?: string
    position?: number
    title?: string
    comment?: string
  }
  reportId?: string
  finalGrade?: {
    percentage?: number
    letter?: string
  }
  categoryGrades?: Array<{
    category: {
      id: string
      name: string
      colour?: string
    }
    percentage?: number
    letter?: string
  }>
  attendance?: {
    presentPercentage: number
    totalLessons: number
    presentCount: number
    absentCount: number
    lateCount: number
  }
}

export const reportsApi = {
  async getClassReports(classId: string, termId?: string): Promise<ClassReportResponse> {
    const params = termId ? { termId } : {}
    const response = await apiClient.get<ClassReportResponse>(`/classes/${classId}/reports`, {
      params,
    })
    return response.data
  },

  async generateReports(data: GenerateReportsRequest): Promise<{ jobId: string }> {
    const response = await apiClient.post<{ jobId: string }>('/reports', data)
    return response.data
  },

  getReportStreamUrl(jobId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080'
    return `${baseUrl}/reports/${jobId}/stream`
  },

  async getStudentReportCard(
    classId: string,
    studentId: string,
    termId?: string
  ): Promise<StudentReportCard> {
    const params = termId ? { termId } : {}
    const response = await apiClient.get<StudentReportCard>(
      `/classes/${classId}/students/${studentId}/report-card`,
      { params }
    )
    return response.data
  },

  async updateReportComment(reportId: string, comment: string): Promise<void> {
    await apiClient.patch(`/reports/${reportId}/comment`, { comment })
  },

  async deleteReport(reportId: string): Promise<void> {
    await apiClient.delete(`/reports/${reportId}`)
  },
}

// Helper to trigger file download
function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Connect API - /connect endpoints

export type PostType = 'ANNOUNCEMENT' | 'MESSAGE' | 'REMINDER' | 'MOMENT' | 'EVENT' | 'ASSIGNMENT'
export type PostVisibility = 'EVERYONE' | 'TAGGED'
export type SpaceType = 'CLASS' | 'COURSE' | 'YEAR_GROUP' | 'DEPARTMENT' | 'CUSTOM'
export type SpaceMemberRole = 'TEACHER' | 'STUDENT' | 'GUARDIAN'

export interface SpacesResponse {
  schoolName: string
  spaces: SpaceSummary[]
  canEnableMore?: boolean
}

export interface SpaceSummary {
  id: string
  type: SpaceType
  classId?: string
  name: string
  colour?: string
  memberCount: number
  latestPostPreview?: string
  latestPostAt?: string
  createdAt: string
  isEnabled?: boolean
}

export interface PostAttachment {
  id: string
  postId: string
  fileName: string
  contentType: string
  fileSize: number
  sortOrder: number
  url?: string
}

export interface SpacePost {
  id: string
  spaceId?: string
  type: PostType
  title?: string
  body: string
  isPinned: boolean
  visibility: PostVisibility
  showAuthor: boolean
  authorName?: string
  contextName?: string
  taggedStudentIds: string[]
  createdBy: string
  createdAt: string
  updatedAt: string
  eventDate?: string
  eventEndDate?: string
  eventAllDay?: boolean
  eventLocation?: string
  eventId?: string
  assignment?: PostAssignment
  attachments?: PostAttachment[]
  isNew?: boolean
}

export interface PostAssignment {
  id: string
  name: string
  dateDue?: string
  dateAssigned?: string
  submissionCount: number
  totalStudents: number
  categoryName?: string
}

export interface SpaceMember {
  userId?: string
  name: string
  role: SpaceMemberRole
  email?: string
  photoUrl?: string
  studentId?: string
  inviteStatus?: 'pending' | 'expired' | 'accepted' | null
}

export interface CreatePostRequest {
  type: PostType
  title?: string
  body: string
  showAuthor?: boolean
  taggedStudentIds?: string[]
  visibility?: PostVisibility
  assignmentId?: string
}

export interface UpdatePostRequest {
  type?: PostType
  title?: string
  body?: string
  taggedStudentIds?: string[]
  visibility?: PostVisibility
}

export const connectApi = {
  async getSpaces(): Promise<SpacesResponse> {
    const response = await apiClient.get<SpacesResponse>('/connect/spaces')
    return response.data
  },

  async getSpace(spaceId: string): Promise<SpaceSummary> {
    const response = await apiClient.get<SpaceSummary>(`/connect/spaces/${spaceId}`)
    return response.data
  },

  async getSpaceMembers(spaceId: string): Promise<SpaceMember[]> {
    const response = await apiClient.get<SpaceMember[]>(`/connect/spaces/${spaceId}/members`)
    return response.data
  },

  async getSpacePosts(spaceId: string, page = 0, limit = 20): Promise<SpacePost[]> {
    const response = await apiClient.get<SpacePost[]>(`/connect/spaces/${spaceId}/posts`, {
      params: { page, limit },
    })
    return response.data
  },

  async getFeed(page = 0, limit = 20, schoolOnly = false): Promise<SpacePost[]> {
    const params: Record<string, any> = { page, limit }
    if (schoolOnly) params.schoolOnly = true
    const response = await apiClient.get<SpacePost[]>('/connect/feed', { params })
    return response.data
  },

  async createPost(spaceId: string, data: CreatePostRequest): Promise<SpacePost> {
    const response = await apiClient.post<SpacePost>(`/connect/spaces/${spaceId}/posts`, data)
    return response.data
  },

  async createSchoolWidePost(data: CreatePostRequest): Promise<SpacePost> {
    const response = await apiClient.post<SpacePost>('/connect/posts', data)
    return response.data
  },

  async updatePost(spaceId: string, postId: string, data: UpdatePostRequest): Promise<SpacePost> {
    const response = await apiClient.put<SpacePost>(
      `/connect/spaces/${spaceId}/posts/${postId}`,
      data,
    )
    return response.data
  },

  async updateSchoolWidePost(postId: string, data: UpdatePostRequest): Promise<SpacePost> {
    const response = await apiClient.put<SpacePost>(`/connect/posts/${postId}`, data)
    return response.data
  },

  async deletePost(spaceId: string, postId: string): Promise<void> {
    await apiClient.delete(`/connect/spaces/${spaceId}/posts/${postId}`)
  },

  async deleteSchoolWidePost(postId: string): Promise<void> {
    await apiClient.delete(`/connect/posts/${postId}`)
  },

  async pinPost(spaceId: string, postId: string, isPinned: boolean): Promise<SpacePost> {
    const response = await apiClient.put<SpacePost>(
      `/connect/spaces/${spaceId}/posts/${postId}/pin`,
      { isPinned },
    )
    return response.data
  },

  async uploadAttachment(postId: string, file: File, spaceId?: string): Promise<PostAttachment> {
    const formData = new FormData()
    formData.append('file', file)
    const url = spaceId
      ? `/connect/spaces/${spaceId}/posts/${postId}/attachments`
      : `/connect/posts/${postId}/attachments`
    const response = await apiClient.post<PostAttachment>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async deleteAttachment(postId: string, attachmentId: string, spaceId?: string): Promise<void> {
    const url = spaceId
      ? `/connect/spaces/${spaceId}/posts/${postId}/attachments/${attachmentId}`
      : `/connect/posts/${postId}/attachments/${attachmentId}`
    await apiClient.delete(url)
  },

  /** Send invite to a single student */
  async inviteStudent(studentId: string): Promise<void> {
    await apiClient.post(`/connect/invite/student/${studentId}`)
  },

  /** Batch invite all uninvited students in a space */
  async inviteAllStudents(spaceId: string): Promise<{ invited: number }> {
    const response = await apiClient.post<{ invited: number }>(`/connect/spaces/${spaceId}/invite-all`)
    return response.data
  },

  async enableSpace(spaceId: string): Promise<void> {
    await apiClient.post(`/connect/spaces/${spaceId}/enable`)
  },

  async disableSpace(spaceId: string): Promise<void> {
    await apiClient.post(`/connect/spaces/${spaceId}/disable`)
  },
}

// Join API - /join endpoints (student/parent registration via invite link)

export interface ConnectInviteDetails {
  inviteType: string
  name: string
  tenantName: string
  email: string
  isExpired: boolean
  isAccepted: boolean
  inviterName?: string
  classNames: string[]
}

export const joinApi = {
  /** Get public invite details by token */
  async getInviteDetails(token: string): Promise<ConnectInviteDetails> {
    const response = await apiClient.get<ConnectInviteDetails>(`/connect/invites/${token}`)
    return response.data
  },

  /** Register with email/password via the join flow */
  async register(data: { email: string; password: string; displayName?: string }): Promise<void> {
    await apiClient.post('/join/register', data)
  },

  /** Sign in with Google via the join flow */
  async google(data: { idToken: string }): Promise<void> {
    await apiClient.post('/join/google', data)
  },

  /** Accept an invite (link account to student record) */
  async acceptInvite(token: string): Promise<void> {
    await apiClient.post(`/connect/invites/${token}/accept`)
  },
}

// Re-export types
export type { User, Lesson, ClassDetails, Student, Term }
